import { href, type Route } from "./route";

/* The navigation shell's model — which SECTION of the product a route belongs to.
 *
 * ── WHY THERE IS A MODEL AT ALL, RATHER THAN A `className="active"` PER SCREEN ─────────────
 *
 * The shell is rendered by five different screens. "Which section am I in" answered separately
 * in each of them is five copies of one rule, and the first time a route moves house four of
 * them keep saying the old thing with nothing going red. `sectionOf` is the one answer, it is
 * pure, and `nav.test.ts` executes it — the Swift harness cannot.
 *
 * ── THE SELECTION MODEL, AND WHAT IT IS *NOT* ─────────────────────────────────────────────
 *
 * `docs/WEB-APP-PLAN.md` §9.1: `EVERFIT-MAP.md` §29.3 measured the competitor's second column
 * as a page tree in exactly ONE section of six — everywhere else it is saved segments, a thread
 * list, or nothing at all. **What is uniform is the ROLE of that column, not its content: the
 * list you are choosing from, with the third column always the detail of that selection.**
 *
 * So this module owns column ONE — the sections — and deliberately says nothing about what
 * column two holds. Each section's list is rendered by the screen that owns the data behind it,
 * which is what stops the shell needing to load a roster in order to label a nav item. Copying
 * their tree as the universal shape would reproduce it in the one place it happens to be a tree
 * and get the other five wrong, which is the same mistake as reading their 17-field exercise row
 * as a specification (§2b).
 *
 * ── THREE SECTIONS, AND THE LABELS ARE ALWAYS VISIBLE ─────────────────────────────────────
 *
 * ⚠️ NOT BECAUSE THEIRS ARE MISSING — that claim was measured in one state of a hover-expanding
 * rail, was false, and is retracted in §29.2. Because we have THREE sections against their
 * seven, so an icon rail would buy no space and cost the label. §9.4 refuses the hover-collapse
 * for that reason and §12's *hover-only affordances* verdict points the same way.
 */

/** The three sections of the product. A fourth is a PRODUCT decision — see §9.3. */
/* 🔴 FOUR SINCE W72 STEP 1 (2026-09-25) — the fourth is HIS decision, taken in Proposal 1 (*"This week ·
   Clients · Library · Inbox. Four."*), which is what this type's own comment always said a fourth needed.
   Automation, On-demand, Forums and Payments stay OUT, not stubbed (his 2026-08-29 word) — pinned by the
   harness as a SET of labels. */
export type SectionId = "dashboard" | "clients" | "library" | "inbox";

export type Section = {
  readonly id: SectionId;
  readonly label: string;
  /** Where the section lands when you choose it. Built by `href()`, never written by hand. */
  readonly route: Route;
  /**
   * Which icon stands beside the section's label in the sidebar (and alone in the phone's bottom bar).
   *
   * ⚠️ AN ID, NOT A COMPONENT. This module is PURE — `nav.test.ts` runs it and the Swift harness
   * reads it — so a `ReactNode` here would make it unimportable by its own test. The shell owns
   * the drawing; this owns which one.
   */
  readonly icon: SectionIcon;
};

/** The glyphs the rail can draw. Closed, so a section cannot name one that does not exist. */
/* W81: the icon names are the ONE icon module's (`components/Icon.tsx`, Lucide). */
export type SectionIcon = "dashboard" | "clients" | "library" | "inbox";

/**
 * The sections, in the order they are shown.
 *
 * ⚠️ THE ORDER IS THE COACH'S DAY, NOT AN ALPHABET. `EVERFIT-MAP.md` §11 timed eight coach jobs
 * and the ones a day is mostly made of — "who needs me today" (job 4) and "who has quietly
 * stopped" (job 7) — are both answered by the roster, so Clients is first. Library is what you
 * open in order to act on what the roster told you. Inbox is where somebody else sets the
 * agenda, which is why it is last rather than first.
 */
/* ⚠️ THIS WEEK IS FIRST (Proposal 1): the coach's day starts with the loop — who trained, who missed,
   who has nothing next — and the roster is the directory behind it, one click on. */
export const SECTIONS: readonly Section[] = [
  { id: "dashboard", label: "Dashboard", route: { name: "dashboard" }, icon: "dashboard" },
  { id: "clients", label: "Clients", route: { name: "roster" }, icon: "clients" },
  { id: "library", label: "Library", route: { name: "workouts" }, icon: "library" },
  { id: "inbox", label: "Inbox", route: { name: "inbox" }, icon: "inbox" },
] as const;

/**
 * Which section a route belongs to, or `null` for an address this app does not have.
 *
 * ⚠️ THE SWITCH IS EXHAUSTIVE ON PURPOSE, AND THAT IS THE POINT OF WRITING IT THIS WAY. A new
 * route added to `Route` without a section here fails to COMPILE rather than quietly rendering
 * a shell with nothing marked current — `BodySkeleton`'s rule in `CLAUDE.md`, where an
 * exhaustive switch means a new joint cannot silently draw nothing. A `default:` arm returning
 * `null` would compile forever and lose the guarantee; V6-12 records a `default:` nobody read
 * shipping eight type styles at the wrong weight.
 *
 * `unknown` is `null` rather than `"clients"` for the reason `parseRoute` refuses the same
 * fallback: an unrecognised address that highlights Clients is a stale link landing somewhere
 * plausible with nothing to notice.
 */
export function sectionOf(route: Route): SectionId | null {
  switch (route.name) {
    // ⚠️ AN INVITATION BELONGS TO NO SECTION, AND `null` IS THE HONEST ANSWER RATHER THAN A
    // FALLBACK. The person opening it is not a coach and has no rail: highlighting Clients would
    // point them at a console they cannot open. Same reasoning as `unknown` below — an address
    // that lights up something plausible is worse than one that lights up nothing.
    case "join":
      return null;
    case "dashboard":
    // A review is reached from the Dashboard's queue and returns to it (`‹ Review queue`, W82 §2): the sidebar keeps the
    // coach where the work came from.
    case "review":
    case "sessionOverview":
    // Next week's builder sits over the review (2026-10-07): the coach has not left the Dashboard's work.
    case "reviewNextWeek":
      return "dashboard";
    case "roster":
    case "client":
    // William's item 4: the builder over a client's week — the coach has not left the client.
    case "clientWorkoutNew":
    case "clientWorkout":
    // The reviewer seat lives under Clients (its `Reviewing` group sits on the roster page, apart from his clients).
    case "reviewing":
    case "reviewingDay":
    case "reviewingWeek":
      return "clients";
    case "workouts":
    case "workoutNew":
    case "workout":
    // W17d's overview is the same section as the builder it sits beside — the rail marks Library
    // current while either window is open, because the coach has not left the library.
    case "workoutOverview":
    // ⚠️ THE EXERCISE SHELF IS THE **SAME SECTION**, NOT A FOURTH ONE (W1, 2026-09-18). It has its
    // own addresses so a shelf can be deep-linked and an editor can be a window, but a fourth rail
    // entry is a PRODUCT decision — `SectionId`'s own comment says so and it is HIS — and the
    // phone's model is one Library holding two lists. Same section, two addresses, and the
    // compatibility rule is kept: breadth may differ between the surfaces, the MODEL may not.
    case "exercises":
    case "exerciseNew":
    case "exercise":
    // ⚠️ PROGRAMS IS THE **SAME** SECTION, FOR THE THIRD TIME AND THE SAME REASON. A fourth rail
    // entry is a PRODUCT decision — `SectionId`'s own comment says so and it is HIS — and a
    // program is a library object made of library objects: it holds nothing but references to
    // the coach's own templates. Three addresses, one section, the rail marking Library current
    // while a coach plans a block.
    case "programs":
    case "programNew":
    case "program":
    // Equipment too — a fourth LIST in the Library, never a fourth section (his, 2026-09-23).
    case "equipment":
      return "library";
    case "inbox":
    case "thread":
      return "inbox";
    // ⚠️ `page` IS DELIBERATELY OUTSIDE THE THREE SECTIONS, and null here is a statement rather
    // than a gap. `SectionId`'s own comment records that a FOURTH section is a PRODUCT decision —
    // his — so the coach's own page is reached from the ACCOUNT row instead, which is also where
    // it lives on the phone ("Your coaching page" sits on the You tab, not in the tab bar). The
    // rail marks nothing current while you are on it, exactly as it does for the account itself.
    // ⚠️ W72 STEP 1: THE INTAKE INBOX MOVED UNDER THE ACCOUNT, with the page and the identity (Fable's
    // build brief) — the rail carries the four places a coach works; the account carries what is
    // about THEM. It was the Clients section before.
    case "requests":
    case "page":
    // ⚠️ THE PUBLIC IDENTITY IS OUTSIDE THE THREE SECTIONS, like the coach's own page and for the
    // same reason: it is about the PERSON rather than a place in the work, and it is reached from
    // the account row. The rail marks nothing current while you stand on it.
    case "identity":
    // W72 step 8 — the account's `Your data` and `What changed`: about the coach, like the three above.
    case "data":
    case "changes":
    // W82 step 6 — the coach's reminders: about the coach's own day, like the account's other pages.
    case "reminders":
    case "unknown":
      return null;
  }
}

/**
 * What `aria-current` a section's nav item should carry, or `undefined` for none.
 *
 * ⚠️ `"page"` AND `"true"` ARE DIFFERENT CLAIMS AND THE DISTINCTION IS FREE HERE. `aria-current="page"`
 * means *this link points at the page you are on*; on the client detail page, saying that about
 * the Clients link is a small lie a screen reader will read out. `"true"` is the documented
 * value for an ancestor of the current page, so a section CONTAINING where you are gets that
 * instead. One expression, and it is the difference between a nav that announces itself
 * correctly and one that is merely styled correctly.
 */
export function currentMarker(route: Route, section: SectionId): "page" | "true" | undefined {
  if (sectionOf(route) !== section) return undefined;
  const landing = SECTIONS.find((s) => s.id === section);
  if (landing === undefined) return undefined;
  return landing.route.name === route.name ? "page" : "true";
}

/** The address of a section, so no screen writes one. */
export function sectionHref(section: Section): string {
  return href(section.route);
}

/** Which list inside the Library is being shown, or `null` when you are deeper than a list. */

/* `libraryList` — the Library's left sub-nav and its phone copy — retired in W72 step 5: the four parts are ONE
 * underline row under the title (`lib/libraryTabs.ts`, `components/LibraryTabs.tsx`). */


/* ── The sidebar (W81, the pro web step 1, 2026-09-26) ─────────────────────────────────────
 *
 * 🔴 THE COLLAPSING RAIL IS GONE, AND SO ARE ITS THREE RULES (`railIsOpen`, `accountIsOpen`, `lockLabel`). His 2026-08-29
 * "collapses to icons" is SUPERSEDED by his W81 yes: a 232 px sidebar, ALWAYS labelled, with counts, the theme switch and
 * the coach's card (docs/COACH-WORKFLOW-W82.md § "The build", step 1). With nothing to open there is nothing that can open
 * for the pointer and not the keyboard — the defect those rules existed to prevent cannot be written any more.
 */

/* ── THE RAIL COMES BACK, WITH A LOCK (W140, his 2026-10-02 word, REVERSING W81's always-labelled on his own evidence) ──
 *
 * *"make the left menu auto hide and we can have a lock/unlock button so specially the workouts can be a little more wide"*.
 * Fable S1: LOCKED = W81's 232 px labelled sidebar exactly; UNLOCKED (the default, S3) = a ~56 px icon rail IN the layout,
 * and the full sidebar opens as an OVERLAY — never a reflow — while the pointer is over it OR keyboard focus is in it.
 * The 2026-08-29 rules come back by the same names, because the defect they existed to prevent is back with the rail:
 * a sidebar that opens for the pointer and stays shut for the keyboard.
 */

/** Is the full sidebar showing? Locked, or the pointer is over it, or focus is inside it — the three are ORed, so the
 *  keyboard opens it exactly as the pointer does. Escape is the caller's: it masks the two transient facts. */
export function railIsOpen(pinned: boolean, pointerInside: boolean, focusInside: boolean): boolean {
  return pinned || pointerInside || focusInside;
}

/** The lock's name says what PRESSING it does (S2), so a screen reader hears the action, and `aria-pressed` the state. */
export function lockLabel(pinned: boolean): string {
  return pinned ? "Let the menu hide" : "Keep the menu open";
}

/** The stored lock (`viewPrefs` "sidebarLocked"): only "1" locks — anything else, or nothing, is S3's default, UNLOCKED. */
export function lockFromStored(raw: string | null): boolean {
  return raw === "1";
}

/** What a section's badge counts. Two numbers, and only two sections may show one. */
export type ShellCounts = { readonly toReview: number | null; readonly unread: number | null };

/**
 * The count a section's nav item carries, or `null` for none.
 *
 * ⚠️ THE ACCENT IS SPENT ON "WAITING ON YOU" AND NOWHERE ELSE IN THE SIDEBAR (W81 amendment (d)): the landing's count of
 * workouts waiting for a review (`reviewQueue.toReviewCount`, W82 step 2 — it was the needs-you count in step 1) and the
 * Inbox's unread. Clients and Library carry
 * NOTHING — a badge on every item is a sidebar of red. A zero, or a count nobody could read (`null`), draws no badge: an
 * empty red pill says "something is waiting" about nothing.
 */
export function sectionBadge(section: SectionId, counts: ShellCounts): number | null {
  const n = section === "dashboard" ? counts.toReview : section === "inbox" ? counts.unread : null;
  return n !== null && n > 0 ? n : null;
}

/**
 * The coach card's `aria-current`: `"page"` on the account's landing (`Your page`), `"true"` on the account's other four
 * tabs, nothing elsewhere — `currentMarker`'s own distinction, applied to the one item at the sidebar's foot.
 */
export function accountMarker(route: Route): "page" | "true" | undefined {
  if (route.name === "page") return "page";
  return route.name === "identity" || route.name === "requests" || route.name === "data" || route.name === "changes"
    || route.name === "reminders" ? "true" : undefined;
}

/**
 * One or two initials for an avatar disc.
 *
 * Queued by the design pass as polish for list rows and used first by the account row at the foot
 * of the rail, so it lives here with the rest of the rail's rules rather than being written twice.
 *
 * ⚠️ IT TAKES THE FIRST LETTER OF THE FIRST AND LAST WORDS, NEVER THE FIRST TWO CHARACTERS. `Jo`
 * for "John Bravos" reads as a truncation; `JB` reads as a person. An email address has no
 * surname, so it yields ONE letter rather than inventing a second from the domain.
 */
export function monogram(name: string): string {
  const cleaned = name.trim();
  if (cleaned === "") return "?";
  // An address is one token by definition — splitting on `@` would make `B` out of `.com`.
  const source = cleaned.includes("@") ? (cleaned.split("@")[0] ?? cleaned) : cleaned;
  const words = source.split(/[\s._-]+/).filter((w) => w !== "");
  const first = words[0];
  if (first === undefined) return "?";
  const last = words.length > 1 ? words[words.length - 1] : undefined;
  const a = [...first][0] ?? "";
  const b = last === undefined ? "" : ([...last][0] ?? "");
  const out = (a + b).toUpperCase();
  return out === "" ? "?" : out;
}
